import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type ProvisionalReviewDetail } from '../../api.ts';
import { FormModal, FormSection, ReasonDialog } from '../../components/FormModal.tsx';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Loading,
  PageHeader,
  Panel,
  Textarea,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';

const STATUS_TONE = { pending: 'info', confirmed: 'ok', cadastre_error: 'off' } as const;

type Answer = 'confirmed' | 'cadastre_error';

/**
 * Resposta do gestor ao título provisório no Master: só contrato, valor e competência (ADR 0094).
 * Cada resposta abre a sua janela (guia visual, Modal): confirmar com observação opcional e erro de
 * cadastro com o motivo obrigatório.
 */
export function ProvisionalReviewPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<Answer | null>(null);
  const [note, setNote] = useState('');
  const key = ['provisional-review', id];
  const data = useQuery({
    queryKey: key,
    queryFn: () => api.get<ProvisionalReviewDetail>(`/finance/provisional-reviews/${id}`),
  });
  const answer = useMutation({
    mutationFn: (value: { answer: Answer; note?: string }) =>
      api.post(`/finance/provisional-reviews/${id}/answer`, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      close();
    },
  });
  const show = (which: Answer) => {
    answer.reset();
    setNote('');
    setOpen(which);
  };
  function close() {
    setOpen(null);
    setNote('');
  }
  const d = data.data;
  const item = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
  return (
    <>
      <PageHeader
        title={t('finance.provisionalReview.title')}
        description={t('finance.provisionalReview.about')}
      />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {d && (
        <Panel>
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[d.status]}>
                {t(`finance.provisionalReview.statuses.${d.status}`)}
              </Badge>
              <span className="text-sm text-muted">
                {t(`finance.provisionalReview.reasons.${d.reason}`)}
              </span>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              {item(t('finance.provisionalReview.fields.payable'), d.payableCode)}
              {item(
                t('finance.provisionalReview.fields.contract'),
                `${d.contractCode} · ${d.contractTitle}`,
              )}
              {item(t('finance.provisionalReview.fields.partner'), d.partnerName)}
              {item(t('finance.provisionalReview.fields.competence'), d.competence)}
              {item(
                t('finance.provisionalReview.fields.amount'),
                fmt.money(d.grossAmount, d.currency),
              )}
              {item(t('finance.provisionalReview.fields.dueDate'), fmt.date(d.firstDueDate))}
              {item(t('finance.provisionalReview.fields.firstDeadline'), fmt.date(d.firstDeadline))}
              {item(t('finance.provisionalReview.fields.finalDeadline'), fmt.date(d.finalDeadline))}
            </dl>
            {d.answerNote && <p className="text-sm">{d.answerNote}</p>}
            {d.supplierNoticeAt && (
              <p className="text-sm text-muted">{t('finance.provisionalReview.supplierNotice')}</p>
            )}
            {d.canAnswer && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" onClick={() => show('cadastre_error')}>
                  {t('finance.provisionalReview.reportError')}
                </Button>
                <Button onClick={() => show('confirmed')}>
                  {t('finance.provisionalReview.confirm')}
                </Button>
              </div>
            )}
          </div>
        </Panel>
      )}
      <FormModal
        open={open === 'confirmed'}
        size="md"
        title={t('finance.provisionalReview.confirmTitle')}
        description={t('finance.provisionalReview.confirmAbout')}
        dirty={note.trim() !== ''}
        saving={answer.isPending}
        error={answer.error}
        saveLabel={t('finance.provisionalReview.confirm')}
        requiredLegend={false}
        onSave={() =>
          answer.mutate({ answer: 'confirmed', ...(note.trim() ? { note: note.trim() } : {}) })
        }
        onClose={close}
      >
        <FormSection columns={1}>
          <Field label={t('finance.provisionalReview.fields.note')}>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </Field>
        </FormSection>
      </FormModal>
      <ReasonDialog
        open={open === 'cadastre_error'}
        title={t('finance.provisionalReview.errorTitle')}
        description={t('finance.provisionalReview.errorAbout')}
        fieldLabel={t('finance.provisionalReview.fields.note')}
        confirmLabel={t('finance.provisionalReview.reportError')}
        tone="danger"
        pending={answer.isPending}
        error={answer.error}
        onConfirm={(reason) => answer.mutate({ answer: 'cadastre_error', note: reason })}
        onClose={close}
      />
    </>
  );
}
